<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <h4 class="mb-0 ml-2" style="font-size: 16px">Insurances</h4>
      <a href="#" v-if="hasCurrentUserPermissionAccess('patients', 'create')" style="font-size: 14px"
        @click.prevent="$emit('onEdit', 1)">
        Edit
      </a>
    </div>
    <div v-for="insurance in insurances" :key="insurance.id" class="d-flex flex-column border-top p-2 mt-2"
      style="font-size: 14px;">
      <template v-if="eligibilityEnabled">
        <div>
          
          <strong v-if="insurance.company" style="white-space:normal;">{{ insurance.company?.name }}
          </strong>
          
          <strong v-if="
              insurance.lastVerification &&
                insurance.lastVerification.activeCoverage
            " class="text-success" style="white-space:normal;">
            (Active Coverage)
          </strong>
          <strong v-else class="text-danger" style="white-space:normal;">
            (Inactive Coverage)
          </strong>
        </div>
        <div v-if="insurance.lastVerification">
          <strong>Last verification: </strong>
          <span>
            {{ insurance.lastVerification.verifiedAt | formatDateOnly }}
          </span>
        </div>
        <div class="d-flex justify-content-between">
          <a v-if="insurance.lastVerification" href="#" @click.prevent="viewVerification(insurance.lastVerification)">
            View last verification
          </a>
          <span v-else></span>
          <a href="#" @click.prevent="verifyEligibility(insurance)">Verify</a>
        </div>
      </template>
      <template v-else>
        <template v-if="insurance.company">
          <strong>{{ insurance.company?.name || "" }} </strong>
        </template>
        <eligibility-request-render-modal v-if="showVerificationRequestRenderModal"
          v-model="showVerificationRequestRenderModal" :eligibilityVerificationId="eligibilityVerificationId" />
        <add-edit-eligibility-modal v-if="showAddEditEligibilityModal" v-model="showAddEditEligibilityModal"
          :episodeOfCareId="eoc.id" :patientInsuranceId="patientInsuranceId" @onModify="onModifyAddVerification" />
      </template>
      <template v-if="insurance.company">
        <div v-if="insurance.company?.phoneNumber">
          <strong>Mobile Phone: </strong>
          <span>{{ insurance.company?.phoneNumber }}</span>
        </div>
        <div v-if="insurance.company?.email">
          <strong>Email: </strong>
          <span>
            {{ insurance.company?.email }}
          </span>
        </div>
        <div v-if="insurance.company?.fax">
          <strong>Fax: </strong>
          <span>
            {{ insurance.company?.fax }}
          </span>
        </div>
        <div v-if="insurance.company?.addressLine1">
          <strong>Address: </strong>
          <span>
            {{ insurance.company?.addressLine1 }}
          </span>
        </div>
      </template>
      <div v-if="insurance.memberIdent">
        <strong>Member#: </strong>
        <span>
          {{ insurance.memberIdent }}
        </span>
      </div>
      <div v-if="insurance.groupNo">
        <strong>Group#: </strong>
        <span>
          {{ insurance.groupNo }}
        </span>
      </div>
      <div v-if="insurance.plan">
        <strong>Plan: </strong>
        <span>
          {{ insurance.plan.name }}
        </span>
      </div>
      <div>
        <strong>Coverage: </strong>
        <span>
          {{ insuranceCoverages[insurance.insuranceCoverage] }}
        </span>
      </div>
      <div v-if="insurance.patientRelationship">
        <strong>Relationship: </strong>
        <span>
          {{ insurance.patientRelationship.name }}
        </span>
      </div>
      <div v-if="
          insurance.patientRelationship &&
            insurance.patientRelationship.name != `self`
        ">
        <div v-if="insurance.insuredFirstName">
          <strong>Insured name: </strong>
          <span>
            {{
            formatName(
            insurance.insuredFirstName,
            insurance.insuredMiddleName,
            insurance.insuredLastName
            )
            }}
          </span>
        </div>
        <div v-if="insurance.insuredDob">
          <strong>Insured DOB: </strong>
          <span>
            {{ insurance.insuredDob }}
          </span>
        </div>
        <div v-if="insurance.insuranceCardFrontId">
          <span class="d-flex flex-row text-primary mt-2 hover-pointer"
            @click="viewFullImage(insurance.insuranceCardFrontUrl)">
            <i class="fa fa-picture-o d-block mr-2" aria-hidden="true" style="font-size: 20px"></i>
            <div>Card Front</div>
          </span>
        </div>
        <div v-if="insurance.insuranceCardBackId">
          <span class="d-flex flex-row text-primary mt-2 hover-pointer"
            @click="viewFullImage(insurance.insuranceCardBackUrl)">
            <i class="fa fa-picture-o d-block mr-2" aria-hidden="true" style="font-size: 20px"></i>
            <div>Card Back</div>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import AddEditEligibilityModal from "../eligibility/AddEditEligibilityModal.vue";
import EligibilityRequestRenderModal from "../eligibility/EligibilityRequestRenderModal.vue";
import eligibilityVerificationServer from "../../server/eligibilityVerification";
import serverCore from "../../server/core";
import { groupBy } from "../../helpers/general";
import { formatEoc } from "../../helpers/episodeOfCare";

export default {
  name: "EocViewSummaryInsurance",
  components: { AddEditEligibilityModal, EligibilityRequestRenderModal },
  props: ["eoc", "showing"],
  data() {
    return {
      insurances: [],
      eligibilityVerificationId: null,
      showVerificationRequestRenderModal: false,
      patientInsuranceId: null,
      showAddEditEligibilityModal: false,
      insuranceCoverages: ["Primary", "Secondary", "Other", "Tertiary"]
    };
  },
  mounted() {
    this.getInsuranceData();
  },
  methods: {
    async downloadFullImage(id) {
      const viewUrl = await this.$router.resolve(
        `/view-original-avatar/${serverCore.getToken()}/${id}`
      );
      return viewUrl.href;
    },
    viewFullImage(url) {
      window.open(url, "_blank");
    },
    async getInsuranceData() {
      if (this.eligibilityEnabled) {
        this.fetchVerifications();
      } else {
        let insurances = [];
        for (let i = 0; i < this.eoc.patient.insurances.length; i++) {
          insurances.push({
            ...this.eoc.patient.insurances[i],
            insuranceCardFrontUrl: this.eoc.patient.insurances[i]
              .insuranceCardFrontId
              ? await this.downloadFullImage(
                  this.eoc.patient.insurances[i].insuranceCardFrontId
                )
              : null,
            insuranceCardBackUrl: this.eoc.patient.insurances[i]
              .insuranceCardBackId
              ? await this.downloadFullImage(
                  this.eoc.patient.insurances[i].insuranceCardBackId
                )
              : null
          });
        }
        this.insurances = insurances;
      }
    },
    async fetchVerifications() {
      const verifications = await eligibilityVerificationServer.query({
        episodeOfCareId: [this.eoc.id]
      });
      const verificationsByInsurance = groupBy(
        verifications.records,
        v => v.insurance.id
      );
      const latestVerifications = {};
      for (const [key, value] of verificationsByInsurance) {
        latestVerifications[key] = value.reduce((a, b) => {
          return new Date(a.verifiedAt) > new Date(b.verifiedAt) ? a : b;
        });
      }

      if (this.eoc.patient.insurances) {
        let insurances = [];
        for (let i = 0; i < this.eoc.patient.insurances.length; i++) {
          insurances.push({
            ...this.eoc.patient.insurances[i],
            ...(latestVerifications[this.eoc.patient.insurances[i].id] && {
              lastVerification:
                latestVerifications[this.eoc.patient.insurances[i].company.id]
            }),
            insuranceCardFrontUrl: this.eoc.patient.insurances[i]
              .insuranceCardFrontId
              ? await this.downloadFullImage(
                  this.eoc.patient.insurances[i].insuranceCardFrontId
                )
              : null,
            insuranceCardBackUrl: this.eoc.patient.insurances[i]
              .insuranceCardBackId
              ? await this.downloadFullImage(
                  this.eoc.patient.insurances[i].insuranceCardBackId
                )
              : null
          });
        }
        this.insurances = insurances;
      }
    },
    viewVerification(verification) {
      this.eligibilityVerificationId = verification.id;
      this.showVerificationRequestRenderModal = true;
    },
    verifyEligibility(patientInsurance) {
      this.patientInsuranceId = patientInsurance.id;
      this.showAddEditEligibilityModal = true;
    },
    onModifyAddVerification(params) {
      if (params?.showResultModal) {
        this.showVerificationRequestRenderModal = true;
        this.eligibilityVerificationId = params?.verificationId;
      }
      this.fetchVerifications();
    },
    formatName(firstName, middleName, lastName) {
      return formatEoc(firstName, middleName, "", lastName);
    }
  },
  computed: {
    ...mapGetters("layout", [
      "hasCurrentUserDocumentPermission",
      "hasCurrentUserPermissionAccess"
    ]),
    ...mapState("layout", ["isAbilityActive"]),
    eligibilityEnabled() {
      return (
        this.isAbilityActive &&
        this.hasCurrentUserDocumentPermission("eligibility")
      );
    }
  },
  watch: {
    showing(newValue) {
      if (newValue) {
        this.getInsuranceData();
      }
    }
  }
};
</script>

<style></style>
